import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, RefreshCw, Thermometer, Wind, Droplets, Sun, CloudRain, Cloud, Search, Shield, AlertTriangle, Check, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { OutfitDisplay, heroImage } from '../components/OutfitSilhouette';
import { getOutfitRecommendation, outfitItems, accessories, activityTypes } from '../utils/outfitEngine';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const accessoryIcons = {
  cap: '🧢', beanie: '🧶', gloves: '🧤', sunglasses: '🕶️',
  bottle: '💧', sunscreen: '🧴', towel: '🏋️', mat: '🧘', changeClothes: '👕',
};

const severityConfig = {
  good: { color: '#22c55e', bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.15)' },
  caution: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.15)' },
  warning: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.15)' },
};

const i18n = {
  fr: {
    title: 'Votre tenue du jour',
    subtitle: 'Conseils personnalisés selon la météo locale',
    usePosition: 'Utiliser ma position',
    chooseCity: 'ou rechercher une ville',
    refresh: 'Actualiser',
    feelsLike: 'Ressenti',
    wind: 'Vent',
    humidity: 'Humidité',
    uv: 'UV',
    outfit: 'Votre sélection',
    accessoriesTitle: 'Accessoires & équipement',
    activity: 'Activité',
    activityTip: 'Conseil',
    safetyTip: 'Sécurité',
    rgpdNotice: 'Position utilisée uniquement pour la météo locale.',
    error: 'Impossible de récupérer la météo. Réessayez ou choisissez votre ville.',
    loading: 'Analyse météo en cours...',
    loadingGeo: 'Détection de votre position...',
    placeholder: 'Bruxelles, 1050, Ixelles...',
    top: 'Haut', layer: 'Couche', bottom: 'Bas', shoes: 'Chaussures',
    equipTip: 'Prochainement',
    equipDesc: 'Check-list séance personnalisée et recommandations équipement.',
    back: 'Retour',
    heroTitle: 'Prêt à bouger ?',
    heroDesc: 'Découvrez la tenue idéale pour votre séance, adaptée en temps réel à la météo de votre ville.',
    lookLabel: 'Le look',
    detailsLabel: 'Le détail',
  },
  en: {
    title: 'Your outfit of the day',
    subtitle: 'Personalized advice based on local weather',
    usePosition: 'Use my location',
    chooseCity: 'or search a city',
    refresh: 'Refresh',
    feelsLike: 'Feels like',
    wind: 'Wind',
    humidity: 'Humidity',
    uv: 'UV',
    outfit: 'Your selection',
    accessoriesTitle: 'Accessories & gear',
    activity: 'Activity',
    activityTip: 'Tip',
    safetyTip: 'Safety',
    rgpdNotice: 'Location only used for local weather.',
    error: 'Unable to retrieve weather. Try again or choose your city.',
    loading: 'Analyzing weather...',
    loadingGeo: 'Detecting your location...',
    placeholder: 'Brussels, 1050, Ixelles...',
    top: 'Top', layer: 'Layer', bottom: 'Bottom', shoes: 'Shoes',
    equipTip: 'Coming soon',
    equipDesc: 'Personalized session checklist and equipment recommendations.',
    back: 'Back',
    heroTitle: 'Ready to move?',
    heroDesc: 'Discover the perfect outfit for your workout, adapted in real-time to your local weather.',
    lookLabel: 'The look',
    detailsLabel: 'The details',
  },
  nl: {
    title: 'Uw outfit van de dag',
    subtitle: 'Gepersonaliseerd advies op basis van lokaal weer',
    usePosition: 'Gebruik mijn locatie',
    chooseCity: 'of zoek een stad',
    refresh: 'Vernieuwen',
    feelsLike: 'Gevoelstemp.',
    wind: 'Wind',
    humidity: 'Vochtigheid',
    uv: 'UV',
    outfit: 'Uw selectie',
    accessoriesTitle: 'Accessoires & uitrusting',
    activity: 'Activiteit',
    activityTip: 'Advies',
    safetyTip: 'Veiligheid',
    rgpdNotice: 'Locatie alleen gebruikt voor lokaal weer.',
    error: 'Kan weer niet ophalen. Probeer opnieuw of kies uw stad.',
    loading: 'Weer analyseren...',
    loadingGeo: 'Locatie detecteren...',
    placeholder: 'Brussel, 1050, Elsene...',
    top: 'Boven', layer: 'Laag', bottom: 'Onder', shoes: 'Schoenen',
    equipTip: 'Binnenkort',
    equipDesc: 'Gepersonaliseerde sessiechecklist en uitrustingsaanbevelingen.',
    back: 'Terug',
    heroTitle: 'Klaar om te bewegen?',
    heroDesc: 'Ontdek de perfecte outfit voor je training, aangepast aan het lokale weer.',
    lookLabel: 'De look',
    detailsLabel: 'De details',
  },
};

function WeatherIcon({ code, isDay, size = 28 }) {
  const color = isDay ? '#f59e0b' : '#94a3b8';
  if (code >= 1192 || code === 1087) return <CloudRain size={size} className="text-blue-400" />;
  if (code >= 1063) return <CloudRain size={size} className="text-blue-300" />;
  if (code >= 1003 && code < 1063) return <Cloud size={size} className="text-gray-400" />;
  return <Sun size={size} style={{ color }} />;
}

function isRainyWeather(weather) {
  return weather && (weather.precip_mm > 0.5 || weather.condition_code >= 1063);
}

export default function OutfitPage() {
  const { lang } = useTranslation();
  const t = i18n[lang] || i18n.fr;

  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [activity, setActivity] = useState('general');
  const [recommendation, setRecommendation] = useState(null);
  const [autoGeoStarted, setAutoGeoStarted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const fetchWeather = useCallback(async (params) => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${API_URL}/api/weather?${qs}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setWeather(data);
      setRecommendation(getOutfitRecommendation(data, activity, lang));
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }, [activity, lang, t.error]);

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) {
      setError(t.error);
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchWeather({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { setError(t.error); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [fetchWeather, t.error]);

  // Auto-detect geolocation on page load
  useEffect(() => {
    if (autoGeoStarted || weather) return;
    setAutoGeoStarted(true);
    
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          fetchWeather({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {
          // If geolocation fails silently, just stop loading and let user choose
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, [autoGeoStarted, weather, fetchWeather]);

  const handleCitySearch = useCallback((e) => {
    e.preventDefault();
    if (cityInput.trim()) fetchWeather({ city: cityInput.trim() });
  }, [cityInput, fetchWeather]);

  useEffect(() => {
    if (weather) setRecommendation(getOutfitRecommendation(weather, activity, lang));
  }, [activity, weather, lang]);

  const sev = recommendation ? severityConfig[recommendation.severity] : severityConfig.good;
  const rainy = isRainyWeather(weather);

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/explore" className="flex items-center gap-2 text-white/40 hover:text-white transition-colors text-sm group" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            {t.back}
          </Link>
          {weather && (
            <button
              onClick={() => fetchWeather(weather.city ? { city: weather.city } : {})}
              className="p-2 rounded-full text-white/30 hover:text-white hover:bg-white/5 transition-all"
              data-testid="refresh-weather"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ========== HERO / LOCATION PROMPT ========== */}
      {!weather && !loading && !error && (
        <div className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImage} alt="" className="w-full h-full object-cover opacity-[0.12]" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0a0a0f]/50 to-[#0a0a0f]" />
          </div>

          <div className="relative max-w-xl mx-auto px-4 sm:px-6 pt-20 pb-24 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="outfit-hero-title">
                {t.heroTitle}
              </h1>
              <p className="text-white/35 text-base sm:text-lg mb-12 max-w-md mx-auto leading-relaxed">
                {t.heroDesc}
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }} className="space-y-4">
              <Button
                onClick={handleGeolocate}
                className="w-full max-w-sm mx-auto py-6 text-base font-semibold rounded-2xl"
                data-testid="geolocate-btn"
                style={{ background: 'linear-gradient(135deg, #3BADE5 0%, #22c55e 100%)' }}
              >
                <MapPin className="w-5 h-5 mr-2.5" /> {t.usePosition}
              </Button>
              <div className="text-white/20 text-sm">{t.chooseCity}</div>
              <form onSubmit={handleCitySearch} className="flex gap-2 max-w-sm mx-auto">
                <input
                  type="text" value={cityInput} onChange={e => setCityInput(e.target.value)}
                  placeholder={t.placeholder}
                  className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#3BADE5]/40 focus:bg-white/[0.06] transition-all"
                  data-testid="city-input"
                />
                <Button type="submit" className="rounded-xl px-5" data-testid="city-search-btn" style={{ background: '#3BADE5' }}>
                  <Search className="w-4 h-4" />
                </Button>
              </form>
              <div className="flex items-center justify-center gap-1.5 text-white/12 text-xs pt-2">
                <Shield className="w-3 h-3" />
                <span>{t.rgpdNotice}</span>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center gap-4 py-28">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }} className="w-12 h-12 rounded-full border-2 border-white/[0.06] border-t-[#3BADE5]" />
          <span className="text-white/30 text-sm">{!weather && autoGeoStarted ? (t.loadingGeo || t.loading) : t.loading}</span>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="max-w-md mx-auto px-4 pt-16">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-red-500/[0.06] border border-red-500/10 rounded-2xl p-6 text-center">
            <p className="text-red-400/80 text-sm mb-4">{error}</p>
            <form onSubmit={handleCitySearch} className="flex gap-2">
              <input
                type="text" value={cityInput} onChange={e => setCityInput(e.target.value)}
                placeholder={t.placeholder}
                className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-[#3BADE5]/40 transition-all"
                data-testid="city-input"
              />
              <Button type="submit" className="rounded-xl px-5" data-testid="city-search-btn" style={{ background: '#3BADE5' }}>
                <Search className="w-4 h-4" />
              </Button>
            </form>
          </motion.div>
        </div>
      )}

      {/* ========== RESULTS ========== */}
      {weather && recommendation && !loading && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

          {/* Title + weather pill */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{t.title}</h1>
              <p className="text-white/30 text-sm mt-1">{t.subtitle}</p>
            </div>
            <div className="flex items-center gap-3 bg-white/[0.04] border border-white/[0.06] rounded-2xl px-5 py-3">
              <WeatherIcon code={weather.condition_code} isDay={weather.is_day} />
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-[#3BADE5]" />
                <span className="text-white/70 text-sm font-medium">{weather.city}</span>
              </div>
              <div className="flex items-baseline gap-0.5 ml-1">
                <span className="text-2xl font-bold text-white">{Math.round(weather.temp_c)}</span>
                <span className="text-white/40 text-sm">°C</span>
              </div>
            </div>
          </div>

          {/* Weather metrics */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { icon: <Thermometer className="w-4 h-4 text-orange-400" />, label: t.feelsLike, value: `${Math.round(weather.feels_like_c)}°` },
              { icon: <Wind className="w-4 h-4 text-blue-300" />, label: t.wind, value: `${Math.round(weather.wind_kph)} km/h` },
              { icon: <Droplets className="w-4 h-4 text-cyan-400" />, label: t.humidity, value: `${weather.humidity}%` },
              { icon: <Sun className="w-4 h-4 text-yellow-400" />, label: t.uv, value: weather.uv },
            ].map((m, i) => (
              <motion.div key={m.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 + 0.1 }} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-3 text-center">
                <div className="flex justify-center mb-1.5">{m.icon}</div>
                <p className="text-white/30 text-[10px] uppercase tracking-wider">{m.label}</p>
                <p className="text-white font-semibold text-sm mt-0.5">{m.value}</p>
              </motion.div>
            ))}
          </div>

          {/* Activity selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-6 scrollbar-hide">
            <span className="text-xs text-white/25 whitespace-nowrap mr-1">{t.activity}:</span>
            {activityTypes.map(act => (
              <button
                key={act.id}
                onClick={() => setActivity(act.id)}
                data-testid={`activity-${act.id}`}
                className={`px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                  activity === act.id
                    ? 'bg-[#3BADE5] text-white shadow-lg shadow-[#3BADE5]/20'
                    : 'bg-white/[0.03] text-white/40 hover:bg-white/[0.06] hover:text-white/60 border border-white/[0.04]'
                }`}
              >
                {act.label[lang] || act.label.fr}
              </button>
            ))}
          </div>

          {/* Severity message */}
          <motion.div key={recommendation.message} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-4 sm:p-5 mb-8" style={{ background: sev.bg, border: `1px solid ${sev.border}` }}>
            <p className="text-sm font-medium" style={{ color: sev.color }}>{recommendation.message}</p>
            {recommendation.safetyTip && (
              <p className="text-xs mt-2 flex items-center gap-1.5 opacity-80" style={{ color: sev.color }}>
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {recommendation.safetyTip}
              </p>
            )}
          </motion.div>

          {/* ========== OUTFIT SHOWCASE ========== */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

            {/* Left: Flat-lay image */}
            <div className="lg:col-span-7">
              <div className="bg-white/[0.02] border border-white/[0.05] rounded-3xl p-4 sm:p-6 overflow-hidden">
                <p className="text-white/20 text-[10px] uppercase tracking-[0.2em] font-medium mb-4">{t.lookLabel}</p>
                <AnimatePresence mode="wait">
                  <OutfitDisplay
                    tempCase={recommendation.tempCase}
                    isRainy={rainy}
                    activity={activity}
                  />
                </AnimatePresence>
              </div>
            </div>

            {/* Right: Details */}
            <div className="lg:col-span-5 space-y-3">
              <p className="text-white/20 text-[10px] uppercase tracking-[0.2em] font-medium mb-1">{t.detailsLabel}</p>

              {/* Outfit items */}
              {[
                { key: 'top', items: recommendation.outfit.top },
                { key: 'layer', items: recommendation.outfit.layer },
                { key: 'bottom', items: recommendation.outfit.bottom },
                { key: 'shoes', items: recommendation.outfit.shoes },
              ].filter(g => g.items.length > 0).map((group, i) => (
                <motion.div key={group.key} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 + 0.2 }} className="bg-white/[0.03] border border-white/[0.05] rounded-2xl p-4">
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.15em] mb-2.5 font-medium">{t[group.key]}</p>
                  <div className="space-y-2">
                    {group.items.map(item => (
                      <div key={item} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 rounded-full bg-[#3BADE5]/10 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-[#3BADE5]" />
                        </div>
                        <span className="text-white/80 text-sm">{(outfitItems[item] || {})[lang] || item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}

              {/* Accessories */}
              {recommendation.outfit.accessories.length > 0 && (
                <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="bg-white/[0.03] border border-white/[0.05] rounded-2xl p-4">
                  <p className="text-white/25 text-[10px] uppercase tracking-[0.15em] mb-3 font-medium">{t.accessoriesTitle}</p>
                  <div className="flex flex-wrap gap-2">
                    {recommendation.outfit.accessories.map(acc => (
                      <motion.span key={acc} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] text-xs text-white/70">
                        <span className="text-sm">{accessoryIcons[acc] || '•'}</span>
                        {(accessories[acc] || {})[lang] || acc}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Activity tip */}
              {recommendation.activityTip && (
                <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }} className="bg-[#3BADE5]/[0.04] border border-[#3BADE5]/10 rounded-2xl p-4">
                  <p className="text-[#3BADE5]/50 text-[10px] uppercase tracking-[0.15em] mb-1.5 font-medium">{t.activityTip}</p>
                  <p className="text-white/60 text-sm leading-relaxed">{recommendation.activityTip}</p>
                </motion.div>
              )}

              {/* Coming soon */}
              <div className="border border-dashed border-white/[0.06] rounded-2xl p-4 opacity-40">
                <p className="text-white/30 text-[10px] uppercase tracking-[0.15em] mb-1 font-medium">{t.equipTip}</p>
                <p className="text-white/15 text-xs">{t.equipDesc}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
